import React, { useEffect, useId, useState } from 'react';
import { wrapFunctionalComponent } from 'utils/framework';
import { Flex, Button } from 'components/ui';
import PayPalCheckout from 'utils/PayPalCheckout';
import localeUtils from 'utils/LanguageLocale';
import ButtonGridEN from 'components/RwdBasket/RwdBasketLayout/CostSummary/Buttons/locales/ButtonGrid_en_US';
import ButtonGridFR from 'components/RwdBasket/RwdBasketLayout/CostSummary/Buttons/locales/ButtonGrid_fr_CA';
import { colors } from 'style/config';

const { getLocalisedDataFromResource } = localeUtils;

const styles = {
    loadingButton: {
        position: 'relative',
        display: 'flex',
        flex: 1
    },
    paypalLoadingButtonBase: {
        width: '100%',
        backgroundColor: colors.paypalYellow,
        transition: 'opacity 0.3s ease-in-out',
        ':disabled': {
            backgroundColor: colors.paypalYellow,
            borderColor: colors.paypalYellow,
            color: colors.white,
            cursor: 'not-allowed'
        },
        '& > div': {
            border: '2px solid rgba(255, 255, 255, 0.3)',
            borderTop: `2px solid ${colors.white}`
        }
    },
    paypalContainerBase: {
        width: '100%',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        transition: 'opacity 0.3s ease-in-out',
        // PayPal creates its own button and a div wrapper around
        // the iframe. This div has a min-width of 150px, which makes it
        // overlap other buttons in small screens
        '& > div': {
            minWidth: '0 !important'
        }
    },
    visible: {
        opacity: 1,
        pointerEvents: 'auto'
    },
    hidden: {
        opacity: 0,
        pointerEvents: 'none'
    }
};

/**
 * Renders the PayPal Checkout SDK button in the basket page's alternate-payments row.
 *
 * Stateless w.r.t. its props at runtime: the effect runs only on mount and unmount,
 * by design. To refresh the SDK's onApprove closure for a new `isBopis` value, the
 * parent must remount this component by changing its React `key` — do NOT add
 * `isBopis` to the effect dependency array. Doing so would re-fire renderPayPalButton
 * mid-mount and re-introduce the stale-closure / in-flight race the mount-only
 * lifecycle exists to avoid. The `eslint-disable react-hooks/exhaustive-deps` below
 * is intentional and load-bearing.
 */
function PayPalCheckoutSdkButton({ isBopis }) {
    const id = useId();
    // Remove colons so the id is a valid CSS selector for window.paypal.Buttons().render(`#${id}`)
    const containerId = `paypal-checkout-container-${id.replace(/:/g, '')}`;
    const [isLoading, setIsLoading] = useState(true);
    const getText = getLocalisedDataFromResource(ButtonGridEN, ButtonGridFR);

    // Mount-only lifecycle. The parent gives this component a key that flips
    // with the basket type, so a basket switch produces a real unmount + mount
    // and this effect re-runs naturally — no prop diffing needed.
    useEffect(() => {
        let cancelled = false;

        (async () => {
            try {
                await PayPalCheckout.preparePaypalCheckout();
                await PayPalCheckout.renderPayPalButton({ containerId, isBopis });
            } catch (error) {
                Sephora.logger.error('[PayPalCheckout] Error rendering PayPal button:', error);
            } finally {
                if (!cancelled) {
                    setIsLoading(false);
                }
            }
        })();

        return () => {
            cancelled = true;
            PayPalCheckout.cleanupPayPalButton({ containerId });
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Flex css={styles.loadingButton}>
            <Button
                variant='primary'
                loading={isLoading}
                disabled={true}
                tabIndex={-1}
                aria-busy={isLoading}
                aria-label={getText('paypalLoading')}
                aria-hidden={!isLoading}
                css={[styles.paypalLoadingButtonBase, isLoading ? styles.visible : styles.hidden]}
            />
            <section
                id={containerId}
                aria-label={getText('paypalButton')}
                aria-hidden={isLoading}
                css={[styles.paypalContainerBase, isLoading ? styles.hidden : styles.visible]}
            />
        </Flex>
    );
}

export default wrapFunctionalComponent(PayPalCheckoutSdkButton, 'PayPalCheckoutSdkButton');
